import React from 'react'
import { 
  TruckIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline'
import type { TransportTypeResponse } from '@/shared/api'

interface ModerationTransportTypesProps {
  currentTypes?: TransportTypeResponse[]
  pendingTypes?: TransportTypeResponse[]
  status: 'approved' | 'pending' | 'rejected'
  label: string
  className?: string
}

interface TransportTypeBadgeProps {
  transport: TransportTypeResponse
  isPending?: boolean
}

const TransportTypeBadge: React.FC<TransportTypeBadgeProps> = ({ 
  transport, 
  isPending = false 
}) => {
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
        isPending 
          ? 'bg-gray-100 text-gray-800 border border-gray-300' 
          : 'bg-green-100 text-green-800'
      }`}
    >
      <TruckIcon className="w-3 h-3 mr-1" />
      {transport.name}
      {transport.requires_license && (
        <span className="ml-1 text-red-600" title="Требуется водительское удостоверение">⚠</span>
      )}
    </span>
  )
}

export const ModerationTransportTypes: React.FC<ModerationTransportTypesProps> = ({
  currentTypes = [],
  pendingTypes = [],
  status,
  label,
  className = ''
}) => {
  const getStatusBadge = () => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircleIcon className="w-3 h-3 mr-1" />
            Утверждено
          </span>
        )
      case 'pending':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <ClockIcon className="w-3 h-3 mr-1" />
            На модерации
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
            Отклонено
          </span>
        )
      default:
        return null
    }
  }

  const showComparison = status === 'pending' && currentTypes.length > 0 && pendingTypes.length > 0
  const displayTypes = status === 'pending' && pendingTypes.length > 0 ? pendingTypes : currentTypes

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Заголовок с статусом */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        {getStatusBadge()}
      </div>

      {showComparison ? (
        // Упрощенное сравнение типов транспорта
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-gray-500 mb-2">Текущие</div>
            <div className="flex flex-wrap gap-2">
              {currentTypes.map((transport) => (
                <TransportTypeBadge
                  key={transport.transport_type_id}
                  transport={transport}
                  isPending={false}
                />
              ))}
            </div>
          </div>
          
          <div>
            <div className="text-xs text-gray-500 mb-2">Предлагаемые</div>
            <div className="flex flex-wrap gap-2">
              {pendingTypes.map((transport) => (
                <TransportTypeBadge
                  key={transport.transport_type_id}
                  transport={transport}
                  isPending={true}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        // Показываем только текущие типы
        <div>
          {displayTypes.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {displayTypes.map((transport) => (
                <TransportTypeBadge
                  key={transport.transport_type_id}
                  transport={transport}
                  isPending={status === 'pending' && pendingTypes.length > 0}
                />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">Типы транспорта не указаны</p>
          )}
        </div>
      )}
    </div>
  )
}
